"use client";

import * as React from "react";
import { useMediaQuery } from "usehooks-ts";

import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "~/components/dialog";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "~/components/drawer";
import { cn } from "~/lib/utils";

interface BaseProps {
	children: React.ReactNode;
}

interface RootCredenzaProps extends BaseProps {
	defaultOpen?: boolean;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

interface CredenzaProps extends BaseProps {
	className?: string;
	asChild?: true;
}

const desktop = "(min-width: 768px)";

function Credenza({ children, ...props }: RootCredenzaProps) {
	const isDesktop = useMediaQuery(desktop);
	const Credenza = isDesktop ? Dialog : Drawer;

	return <Credenza {...props}>{children}</Credenza>;
}

function CredenzaTrigger({ className, children, ...props }: CredenzaProps) {
	const isDesktop = useMediaQuery(desktop);
	const CredenzaTrigger = isDesktop ? DialogTrigger : DrawerTrigger;

	return (
		<CredenzaTrigger className={className} {...props}>
			{children}
		</CredenzaTrigger>
	);
}

function CredenzaClose({ className, children, ...props }: CredenzaProps) {
	const isDesktop = useMediaQuery(desktop);
	const CredenzaClose = isDesktop ? DialogClose : DrawerClose;

	return (
		<CredenzaClose className={className} {...props}>
			{children}
		</CredenzaClose>
	);
}

function CredenzaContent({ className, children, ...props }: CredenzaProps) {
	const isDesktop = useMediaQuery(desktop);
	const CredenzaContent = isDesktop ? DialogContent : DrawerContent;

	return (
		<CredenzaContent className={className} {...props}>
			{children}
		</CredenzaContent>
	);
}

function CredenzaDescription({
	className,
	children,
	...props
}: CredenzaProps) {
	const isDesktop = useMediaQuery(desktop);
	const CredenzaDescription = isDesktop ? DialogDescription : DrawerDescription;

	return (
		<CredenzaDescription className={className} {...props}>
			{children}
		</CredenzaDescription>
	);
}

function CredenzaHeader({ className, children, ...props }: CredenzaProps) {
	const isDesktop = useMediaQuery(desktop);
	const CredenzaHeader = isDesktop ? DialogHeader : DrawerHeader;

	return (
		<CredenzaHeader className={className} {...props}>
			{children}
		</CredenzaHeader>
	);
}

function CredenzaTitle({ className, children, ...props }: CredenzaProps) {
	const isDesktop = useMediaQuery(desktop);
	const CredenzaTitle = isDesktop ? DialogTitle : DrawerTitle;

	return (
		<CredenzaTitle className={className} {...props}>
			{children}
		</CredenzaTitle>
	);
}

function CredenzaBody({ className, children, ...props }: CredenzaProps) {
	return (
		<div className={cn("px-4 md:px-0", className)} {...props}>
			{children}
		</div>
	);
}

function CredenzaFooter({ className, children, ...props }: CredenzaProps) {
	const isDesktop = useMediaQuery(desktop);
	const CredenzaFooter = isDesktop ? DialogFooter : DrawerFooter;

	return (
		<CredenzaFooter className={className} {...props}>
			{children}
		</CredenzaFooter>
	);
}

export {
	Credenza,
	CredenzaBody,
	CredenzaClose,
	CredenzaContent,
	CredenzaDescription,
	CredenzaFooter,
	CredenzaHeader,
	CredenzaTitle,
	CredenzaTrigger,
};
